import React, { useState, useEffect } from "react";

//Component
import Table, { TableRow } from "../../../../component/table";
import PageSelector from "../../../../component/PageSelector";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../../component/modal-popup/customeModal";
import Loader from "react-spinner-loader";
import { getDateWithFormatUTC } from "../../../../utils";
import CreateAndUpdateProductionOrder from "./CreateAndUpdateProductionOrder";
import { useNavigate, useLocation } from "react-router-dom";
import RowsPerPageSelector from "../../../../component/RowsPerPageSelector";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  getProductionData,
  deleteProductionData,
} from "../../../../redux/actions/productionOrder";

function ProductionOrderTable({
  debouncedHandleSearch,
  update,
  setUpdate,
  status,
  updateCounter,
  setUpdateCounter,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // state
  const [page, setPage] = useState(1);
  const [tableLoader, setTableLoader] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [sort, setSort] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [productionData, setProductionData] = useState();

  // Redux
  const dispatch = useDispatch();
  const { productionOrderData } = useSelector((state) => state.productionOrder);
  const { row_count } = useSelector((state) => state.auth);

  // Function to call GET API Production Order List
  const getProductionOrderList = () => {
    setTableLoader(true);
    const request = {
      onSuccess: () => {
        setTableLoader(false);
      },
      onError: () => {
        setTableLoader(false);
      },
    };
    dispatch(
      getProductionData(
        request,
        page,
        debouncedHandleSearch,
        sortOrder,
        sortField,
        row_count,
        status[0] === "all" ? ["completed", "pending", "in progress"] : status
      )
    );
  };

  // Function to call DELETE api to delete Production Order List
  const handleDelete = (id) => {
    const request = {
      production_order_id: id,
      onSuccess: () => {
        setUpdate(update + 1);
        setUpdateCounter(updateCounter + 1);
      },
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(deleteProductionData(request));
  };

  useEffect(() => {
    if (status.length > 0) {
      getProductionOrderList();
    }
  }, [
    update,
    page,
    debouncedHandleSearch,
    sortOrder,
    sortField,
    status,
    row_count,
  ]);

  useEffect(() => {
    if (sort) {
      setPage(1);
      setSortField(sort.headName);
      setSortOrder(sort.type);
    }
  }, [sort]);

  useEffect(() => {
    if (productionData) {
      setModalOpen(true);
    }
  }, [productionData]);

  const showStatus = (status) => {
    switch (status) {
      case "pending": {
        return (
          <div className="flex justify-center items-center w-[90px] py-2 bg-[#F7921E] text-white rounded-md">
            <p className="text-white leading-[100%] font-medium text-[13px] tracking-tight capitalize">
              {status}
            </p>
          </div>
        );
      }

      case "in progress": {
        return (
          <div className="flex justify-center items-center w-[90px] py-2 bg-[--bg-color-500] text-white rounded-md">
            <p className="text-white leading-[100%] font-medium text-[13px] tracking-tight capitalize">
              {status}
            </p>
          </div>
        );
      }

      case "completed": {
        return (
          <p className="text-[#27AE60] leading-[100%] font-medium text-[13px] tracking-tight capitalize">
            {status}
          </p>
        );
      }

      default: {
        return status;
      }
    }
  };

  return (
    <div>
      <Table
        heads={[
          {
            headName: "Production Order Number",
            isSort: true,
            value: "production_order_no",
          },
          {
            headName: "Reference Number",
            isSort: true,
            value: "production_order_reference_no",
          },
          { headName: "Product", isSort: false, value: "product" },
          {
            headName: "Production Start",
            isSort: false,
            value: "planned_date",
          },
          { headName: "Production End", isSort: false, value: "actual_date" },
          { headName: "Status", isSort: true, value: "status" },
          { headName: "Action", isSort: false, value: "Action", class: "w-[100px]"},
        ]}
        tClass="table-fixed w-full border-separate border-spacing-y-2"
        hClass="text-sm leading-2 tracking-tight font-semibold text-[#1E293B] bg-[--bg-color-100]"
        bClass="text-sm"
        thClass={`px-2 py-3 whitespace-nowrap text-left font-semibold first:pl-4 first:rounded-l-md last:rounded-r-md max-w-1/4`}
        setSort={setSort}
        sort={sort}
      >
        {tableLoader ? (
          <tr className="px-2 py-3 whitespace-nowrap">
            <td colSpan={6}>
              <div className="flex justify-center p-2 my-2 whitespace-nowrap w-full">
                <Loader show={tableLoader} />
              </div>
            </td>
          </tr>
        ) : productionOrderData?.data?.length > 0 ? (
          productionOrderData.data.map((element, index) => {
            const { production_order_no, production_order_reference_no, production_order_products, status } =
              element;

            const tableStatus = showStatus(status);
            const products = production_order_products
              .map(
                (item) => `${item?.qty} x ${item?.product_master?.product_name}`
              )
              .join(", ");

            const start_data = production_order_products[0]?.planned_date
              ? getDateWithFormatUTC(production_order_products[0]?.planned_date)
              : "-";
            const end_date = production_order_products[0]?.actual_date
              ? getDateWithFormatUTC(production_order_products[0]?.actual_date)
              : "-";

            return (
              <TableRow
                key={index}
                row={{
                  production_order_no,
                  production_order_reference_no,
                  products,
                  start_data,
                  end_date,
                }}
                tdClass="px-2 py-2 text-sm leading-4 tracking-tight first:pl-4 text-[#1E293B] text-left bg-[--bg-color-50] first:rounded-l-md last:rounded-r-md max-w-1/4 truncate first:font-semibold"
              >
                <td
                  title={status}
                  className="px-2 py-2 text-sm leading-4 tracking-tight first:pl-4 text-[#1E293B] text-left bg-[--bg-color-50] max-w-1/4 truncate"
                >
                  {showStatus(status)}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-center text-sm bg-[--bg-color-50] rounded-r-md leading-4 tracking-tight text-[--bg-color-500] font-semibold">
                  <div className="space-x-3 flex items-center justify-start">
                    <div title="Edit">
                      <span
                        className="fa-light fa-pen text-base cursor-pointer"
                        style={{ color: "#9297a2" }}
                        onClick={() => {
                          navigate("/order/update_production_order", {
                            state: {
                              update: element,
                              allSelectedProducts: null,
                            },
                          });
                        }}
                      ></span>
                    </div>
                    <div title="Delete">
                      <span
                        className="fa-light fa-trash-can text-base cursor-pointer"
                        style={{ color: "#eb5757" }}
                        onClick={() => {
                          swal({
                            content: (
                              <CustomeModal
                                type="delete"
                                title="Are you sure?"
                                content="All of your data will be permanently removed from our servers forever. This action cannot be undone."
                                onDelete={() =>
                                  handleDelete(element.production_order_id)
                                }
                              />
                            ),
                            buttons: false,
                            closeOnClickOutside: false,
                            closeOnEsc: false,
                          });
                        }}
                      ></span>
                    </div>
                  </div>
                </td>
              </TableRow>
            );
          })
        ) : (
          <tr className="px-2 py-3 whitespace-nowrap">
            <td
              colSpan={6}
              className="text-center py-2 text-lg text-[#1E293B] tracking-tight leading-4"
            >
              There are no records to display
            </td>
          </tr>
        )}
      </Table>
      {!tableLoader && (
        <div className="flex justify-between items-center">
          <RowsPerPageSelector />
          <PageSelector
            rowPerPage={row_count}
            page={page}
            setpage={(newPage) => {
              setPage(newPage);
            }}
            totalDataCount={
              productionOrderData ? productionOrderData.totalCount : 0
            }
          />
        </div>
      )}
      <CreateAndUpdateProductionOrder
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        update={update}
        setUpdate={setUpdate}
        productionData={productionData}
        setProductionData={setProductionData}
      />
    </div>
  );
}

export default ProductionOrderTable;
